import { useState, useRef, useCallback, useMemo, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Badge } from '@/components/catalyst/badge'
import { FormSelect } from '@/components/ui/form'
import { Input as CatalystInput } from '@/components/catalyst/input'
import { TrashIcon, CheckIcon, ArrowsPointingOutIcon, XMarkIcon as XClose } from '@heroicons/react/24/outline'
import { FunnelIcon } from '@heroicons/react/20/solid'
import clsx from 'clsx'

// ─── Column resize constants ─────────────────────────────

const COL_WIDTHS_KEY = 'products-table-col-widths'
const DEFAULT_COL_WIDTHS: Record<string, number> = {
  upc: 130, style_no: 90, style_descr: 160, color: 80, size: 55,
  cost_price: 65, selling_price: 65, quantity: 55, tot_cost: 85, tot_selling: 85,
  in_stock: 70, actions: 36,
}
// Floor kept small — earlier 40px (plus per-col minWidth on each <col>
// below) prevented squeezing rarely-used columns narrower than the
// column title text. Relaxed to 30 so a column can be shrunk almost
// to invisible.
const MIN_COL_W = 30
const MAX_COL_W = 400

// ─── Column resize hook ─────────────────────────────────

function useProductColumnWidths() {
  const [widths, setWidths] = useState<Record<string, number>>(() => {
    if (typeof window === 'undefined') return {}
    try { return JSON.parse(localStorage.getItem(COL_WIDTHS_KEY) || '{}') } catch { return {} }
  })

  useEffect(() => { localStorage.setItem(COL_WIDTHS_KEY, JSON.stringify(widths)) }, [widths])

  const getW = (col: string) => widths[col] || DEFAULT_COL_WIDTHS[col] || 80

  const [resizingCol, setResizingCol] = useState<string | null>(null)
  const startX = useRef(0)
  const startW = useRef(0)

  const startResize = useCallback((col: string, e: React.MouseEvent) => {
    e.preventDefault(); e.stopPropagation()
    setResizingCol(col)
    startX.current = e.clientX
    startW.current = widths[col] || DEFAULT_COL_WIDTHS[col] || 80
  }, [widths])

  useEffect(() => {
    if (!resizingCol) return
    const onMove = (e: MouseEvent) => {
      const nw = Math.max(MIN_COL_W, Math.min(MAX_COL_W, startW.current + e.clientX - startX.current))
      setWidths(prev => ({ ...prev, [resizingCol]: nw }))
    }
    const onUp = () => setResizingCol(null)
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [resizingCol])

  return { getW, startResize, isResizing: !!resizingCol }
}

// ─── Types ───────────────────────────────────────────────

export interface Product {
  id: number
  upc: string
  style_no: string | null
  style_descr: string | null
  color: string | null
  size: string | null
  cost_price: number
  selling_price: number
  quantity: number
  in_stock: number
  in_stock_effective: number
  state: string | null
  accepted: boolean
  quantity_in_box: Record<string, number>
  excesses_count: number
  tot_cost: number
  tot_selling: number
}

interface PurchaseOrder {
  id: number
  po_number: string | null
  boxes: string[]
}

interface ProductsTabProps {
  products: Product[]
  orderId: number
  purchaseOrder: PurchaseOrder | null
  currencySymbol: string
  csrfToken: string
  onNotify: (msg: string) => void
}

// ─── Helpers ─────────────────────────────────────────────

function isNotFound(p: Product) { return p.state === 'not_found' }
function isAllFound(p: Product) { return p.state !== 'not_found' }

// ─── Inline Editable Cell ────────────────────────────────

function EditableCell({ value, onSave, type = 'text', className }: {
  value: string | number | null
  onSave: (val: string) => void
  type?: string
  className?: string
}) {
  const original = String(value ?? '')
  const [val, setVal] = useState(original)
  const [editing, setEditing] = useState(false)
  const cellRef = useRef<HTMLSpanElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [width, setWidth] = useState<number | undefined>()

  const commit = () => {
    setEditing(false)
    if (val !== original) onSave(val)
  }

  const startEdit = () => {
    const captured = cellRef.current?.offsetWidth ?? 0
    setWidth(Math.max(captured, 64))
    setEditing(true)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  return (
    <div className="flex h-6 items-center">
      {editing ? (
        <div className="relative inline-flex items-center" style={{ width: width ? `${width + 20}px` : undefined }}>
          <input
            ref={inputRef}
            type={type}
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setVal(original); setEditing(false) } }}
            className="min-w-16 w-full rounded border border-indigo-300 bg-white py-0.5 pl-1 pr-5 text-xs focus:outline-none dark:bg-zinc-800 dark:border-indigo-500/50"
            autoFocus
          />
          {val && (
            <button type="button" onMouseDown={(e) => { e.preventDefault(); setVal('') }}
              className="absolute right-1 text-zinc-300 hover:text-zinc-500 dark:text-zinc-600">
              <XClose className="h-3 w-3" />
            </button>
          )}
        </div>
      ) : (
        <span
          ref={cellRef}
          className={clsx('cursor-pointer border border-transparent border-b-zinc-300 border-dashed px-1 py-0.5 dark:border-b-zinc-600', className)}
          onClick={startEdit}
        >
          {val || '—'}
        </span>
      )}
    </div>
  )
}

// ─── Column Visibility Dropdown ───────────────────────────

function ColumnVisibility({ allColumns, hiddenCols, toggleCol }: {
  allColumns: { key: string; label: string }[]
  hiddenCols: Set<string>
  toggleCol: (col: string) => void
}) {
  const [open, setOpen] = useState(false)
  const btnRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ top: 0, left: 0 })

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (!panelRef.current?.contains(e.target as Node) && !btnRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => {
          if (btnRef.current) { const r = btnRef.current.getBoundingClientRect(); setPos({ top: r.bottom + 2, left: r.right - 160 }) }
          setOpen(!open)
        }}
        className="rounded p-0.5 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
        title="Show/hide columns"
      >
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 1 1-3 0m3 0a1.5 1.5 0 1 0-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 0 1-3 0m3 0a1.5 1.5 0 0 0-3 0m-9.75 0h9.75" />
        </svg>
      </button>
      {open && createPortal(
        <div ref={panelRef} onMouseDown={(e) => e.stopPropagation()} className="fixed z-[100] w-40 rounded-lg border border-zinc-200 bg-white p-1.5 shadow-xl dark:border-zinc-700 dark:bg-zinc-800"
          style={{ top: pos.top, left: pos.left }}>
          {allColumns.map(c => (
            <label key={c.key} className="flex cursor-pointer items-center gap-1.5 rounded px-1.5 py-0.5 text-[10px] text-zinc-700 hover:bg-zinc-50 dark:text-zinc-300 dark:hover:bg-zinc-700/50">
              <input type="checkbox" checked={!hiddenCols.has(c.key)} onChange={() => toggleCol(c.key)} className="h-3 w-3 rounded border-zinc-300 text-indigo-600" />
              {c.label}
            </label>
          ))}
        </div>,
        document.body
      )}
    </>
  )
}

// ─── Column Filter (dropdown with checkboxes) ────────────

function ColumnFilter({ col, products, filterValues, setFilterValues }: {
  col: string
  products: Product[]
  filterValues: Record<string, string>
  setFilterValues: React.Dispatch<React.SetStateAction<Record<string, string>>>
}) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const btnRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const [pos, setPos] = useState({ top: 0, left: 0 })

  // Get unique values for this column
  const uniqueValues = useMemo(() => {
    const vals = new Set<string>()
    products.forEach(p => {
      const v = String((p as unknown as Record<string, unknown>)[col] ?? '').trim()
      if (v) vals.add(v)
    })
    return Array.from(vals).sort()
  }, [products, col])

  // Currently selected values (comma-separated in filterValues)
  const selected = useMemo(() => {
    const fv = filterValues[col]
    if (!fv) return new Set<string>()
    return new Set(fv.split('|||').filter(Boolean))
  }, [filterValues, col])

  const isFiltered = selected.size > 0

  const filtered = search ? uniqueValues.filter(v => v.toLowerCase().includes(search.toLowerCase())) : uniqueValues

  const toggle = (val: string) => {
    const next = new Set(selected)
    if (next.has(val)) next.delete(val)
    else next.add(val)
    // If none selected, clear filter (show all)
    if (next.size === 0) {
      setFilterValues(prev => ({ ...prev, [col]: '' }))
    } else {
      setFilterValues(prev => ({ ...prev, [col]: Array.from(next).join('|||') }))
    }
  }

  const selectAll = () => setFilterValues(prev => ({ ...prev, [col]: '' }))

  const openDropdown = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      setPos({ top: rect.bottom + 2, left: rect.left })
    }
    setOpen(true)
    setSearch('')
    setTimeout(() => searchRef.current?.focus(), 50)
  }

  const panelId = `col-filter-${col}`
  // Close on Escape or click outside
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    const onClick = (e: MouseEvent) => {
      const panel = document.getElementById(panelId)
      if (panel?.contains(e.target as Node)) return
      if (btnRef.current?.contains(e.target as Node)) return
      setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    // Delay to avoid closing on the same click that opened
    const timer = setTimeout(() => document.addEventListener('mousedown', onClick), 50)
    return () => { clearTimeout(timer); document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onClick) }
  }, [open, panelId])

  return (
    <>
      <button
        ref={btnRef}
        onClick={openDropdown}
        className={clsx(
          'inline-flex items-center gap-0.5 rounded p-0.5',
          isFiltered
            ? 'text-indigo-600 dark:text-indigo-400'
            : 'text-zinc-300 hover:text-zinc-500 dark:text-zinc-600 dark:hover:text-zinc-400'
        )}
        title="Filter"
      >
        <FunnelIcon className="h-3 w-3" />
        {isFiltered && <span className="text-[9px] font-semibold">{selected.size}</span>}
      </button>
      {open && createPortal(
        <div
          id={panelId}
          ref={panelRef}
          className="fixed z-[100] w-56 rounded-lg border border-zinc-200 bg-white text-xs shadow-xl dark:border-zinc-700 dark:bg-zinc-800"
          style={{ top: pos.top, left: pos.left }}
        >
          <div className="border-b border-zinc-100 p-2 dark:border-zinc-700">
            <input
              ref={searchRef}
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full rounded border border-zinc-200 px-2 py-1 text-xs focus:border-indigo-400 focus:outline-none dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
            />
          </div>
          <div className="max-h-56 overflow-y-auto p-1.5">
            {isFiltered && (
              <button onClick={selectAll} className="mb-1 w-full rounded px-2 py-1 text-left text-xs text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-500/10">
                Clear filter
              </button>
            )}
            {filtered.map(v => (
              <label key={v} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs text-zinc-700 hover:bg-zinc-50 dark:text-zinc-300 dark:hover:bg-zinc-700/50">
                <input
                  type="checkbox"
                  checked={isFiltered && selected.has(v)}
                  onChange={() => toggle(v)}
                  className="h-3.5 w-3.5 rounded border-zinc-300 text-indigo-600"
                />
                <span className="truncate">{v}</span>
              </label>
            ))}
            {filtered.length === 0 && <p className="px-2 py-1 text-xs text-zinc-400">No matches</p>}
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

// ─── Received Editor (box-based popover) ─────────────────

function ReceivedEditor({ product, csrfToken, onUpdate }: {
  product: Product; csrfToken: string; onUpdate: (p: Product) => void
}) {
  const [open, setOpen] = useState(false)
  const [box, setBox] = useState('1')
  const [qty, setQty] = useState(0)
  const [saving, setSaving] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0 })
  const btnRef = useRef<HTMLButtonElement>(null)

  const boxKeys = Object.keys(product.quantity_in_box || {})
  const allBoxes = Array.from(new Set([...boxKeys, ...Array.from({ length: 10 }, (_, i) => String(i + 1))])).sort((a, b) => Number(a) - Number(b))

  const openEditor = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      setPos({ top: rect.bottom + 4, left: Math.max(8, rect.right - 208) })
    }
    setBox(boxKeys[0] || '1')
    setQty(product.quantity_in_box?.[boxKeys[0] || '1'] || 0)
    setOpen(true)
  }

  const handleBoxChange = (b: string) => { setBox(b); setQty(product.quantity_in_box?.[b] || 0) }

  const handleUpdate = async () => {
    setSaving(true)
    const res = await fetch(`/products/${product.id}/update_received.json`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ box, quantity: qty }),
    })
    if (res.ok) { const data = await res.json(); onUpdate(data); setOpen(false) }
    setSaving(false)
  }

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      const portal = document.getElementById('received-portal')
      if (portal && !portal.contains(e.target as Node) && !btnRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  return (
    <>
      <button
        ref={btnRef}
        onClick={openEditor}
        className="cursor-pointer border border-transparent border-b-zinc-300 border-dashed px-1 py-0.5 font-medium dark:border-b-zinc-600"
      >
        {product.in_stock}
      </button>
      {open && createPortal(
        <div
          id="received-portal"
          className="fixed z-[100] w-52 rounded-lg border border-zinc-200 bg-white p-3 shadow-xl dark:border-zinc-700 dark:bg-zinc-800"
          style={{ top: pos.top, left: pos.left }}
        >
          <div className="mb-2 flex items-center gap-2">
            <label className="shrink-0 text-[10px] text-zinc-500">Box #</label>
            <select value={box} onChange={(e) => handleBoxChange(e.target.value)}
              className="flex-1 rounded border border-zinc-200 px-1 py-0.5 text-xs dark:border-zinc-700 dark:bg-zinc-900 dark:text-white">
              {allBoxes.map(b => <option key={b} value={b}>{b} {product.quantity_in_box?.[b] ? `(${product.quantity_in_box[b]})` : ''}</option>)}
            </select>
          </div>
          <div className="mb-2 flex items-center gap-2">
            <label className="shrink-0 text-[10px] text-zinc-500">Qty</label>
            <input type="number" value={qty} onChange={(e) => setQty(Number(e.target.value))}
              className="flex-1 rounded border border-zinc-200 px-1 py-0.5 text-xs dark:border-zinc-700 dark:bg-zinc-900 dark:text-white" />
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleUpdate} disabled={saving}
              className="rounded bg-indigo-600 px-2 py-1 text-xs font-medium text-white hover:bg-indigo-500 disabled:opacity-50">
              {saving ? '...' : 'Update'}
            </button>
            <button onClick={() => setOpen(false)} className="text-xs text-zinc-400 hover:text-zinc-600">Cancel</button>
          </div>
          {boxKeys.length > 0 && (
            <div className="mt-2 border-t border-zinc-100 pt-2 text-[10px] text-zinc-400 dark:border-zinc-700">
              {boxKeys.map(b => <div key={b}>Box {b}: {product.quantity_in_box[b]}</div>)}
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  )
}

// ─── Products Table ──────────────────────────────────────

function ProductsTable({ products, csrfToken, currencySymbol, onNotify, onProductUpdate, onProductDelete, showPrices, showActions = true, actionColumn }: {
  products: Product[]
  csrfToken: string
  currencySymbol: string
  onNotify: (msg: string) => void
  onProductUpdate: (p: Product) => void
  onProductDelete: (id: number) => void
  showPrices: boolean
  showActions?: boolean
  actionColumn?: (p: Product) => React.ReactNode
}) {
  const { getW, startResize, isResizing } = useProductColumnWidths()
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set()
    try { return new Set(JSON.parse(localStorage.getItem('products-hidden-cols') || '[]')) } catch { return new Set() }
  })
  useEffect(() => { localStorage.setItem('products-hidden-cols', JSON.stringify(Array.from(hiddenCols))) }, [hiddenCols])
  const toggleCol = (col: string) => setHiddenCols(prev => { const n = new Set(prev); if (n.has(col)) n.delete(col); else n.add(col); return n })
  const vis = (col: string) => !hiddenCols.has(col)
  const allColumns = [
    { key: 'upc', label: 'UPC' }, { key: 'style_no', label: 'Style #' }, { key: 'style_descr', label: 'Description' },
    { key: 'color', label: 'Color' }, { key: 'size', label: 'Size' },
    ...(showPrices ? [{ key: 'cost_price', label: 'Cost' }, { key: 'selling_price', label: 'Selling' }] : []),
    { key: 'quantity', label: 'Qty' },
    ...(showPrices ? [{ key: 'tot_cost', label: 'Tot Cost' }, { key: 'tot_selling', label: 'Tot Selling' }] : []),
    { key: 'in_stock', label: 'Received' },
  ]

  const saveField = useCallback((productId: number, field: string, value: string) => {
    fetch(`/products/${productId}.json`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ product: { [field]: value } }),
    })
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) { onProductUpdate(data); onNotify('Saved:Product updated') } })
  }, [csrfToken, onProductUpdate, onNotify])

  const deleteProduct = useCallback((id: number) => {
    if (!confirm('Delete this product?')) return
    fetch(`/products/${id}.json`, { method: 'DELETE', headers: { 'X-CSRF-Token': csrfToken } })
      .then(res => { if (res.ok) { onProductDelete(id); onNotify('Deleted:Product removed') } })
  }, [csrfToken, onProductDelete, onNotify])

  const totals = products.reduce((acc, p) => ({
    quantity: acc.quantity + (p.quantity || 0),
    cost: acc.cost + p.tot_cost,
    selling: acc.selling + p.tot_selling,
    received: acc.received + p.in_stock,
  }), { quantity: 0, cost: 0, selling: 0, received: 0 })

  // ── Filtering ──
  const [filterValues, setFilterValues] = useState<Record<string, string>>({})
  const [sortColumn, setSortColumn] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  const toggleSort = (col: string) => {
    if (sortColumn === col) {
      if (sortDirection === 'asc') setSortDirection('desc')
      else { setSortColumn(null); setSortDirection('asc') }
    } else { setSortColumn(col); setSortDirection('asc') }
  }

  const filteredSorted = useMemo(() => {
    let result = [...products]
    Object.entries(filterValues).forEach(([col, fv]) => {
      if (!fv.trim()) return
      const allowed = new Set(fv.split('|||').filter(Boolean))
      result = result.filter(p => allowed.has(String((p as unknown as Record<string, unknown>)[col] ?? '').trim()))
    })
    if (sortColumn) {
      result.sort((a, b) => {
        const av = (a as unknown as Record<string, unknown>)[sortColumn]
        const bv = (b as unknown as Record<string, unknown>)[sortColumn]
        if (av == null && bv == null) return 0
        if (av == null) return 1
        if (bv == null) return -1
        if (typeof av === 'number' && typeof bv === 'number') return sortDirection === 'asc' ? av - bv : bv - av
        const cmp = String(av).localeCompare(String(bv))
        return sortDirection === 'asc' ? cmp : -cmp
      })
    }
    return result
  }, [products, filterValues, sortColumn, sortDirection])

  if (products.length === 0) {
    return <p className="py-4 text-center text-xs text-zinc-400">No products</p>
  }

  // ── Sortable header helper (returns JSX, not a component) ──
  // min-w-0 + max-w-full + truncate on the label so the header text
  // ellipsizes when the column is narrowed below the title's intrinsic
  // width. Without it the label spills over the next column.
  const sortTh = (col: string, label: string, filterable = false) => (
    <th key={col} className="relative py-2 pl-2 pr-4 select-none" style={{ width: getW(col) }}>
      <span className="flex min-w-0 max-w-full items-center gap-0.5">
        <span className="min-w-0 cursor-pointer truncate" onClick={() => toggleSort(col)}>
          {label}
          {sortColumn === col && <span className="ml-0.5 text-indigo-500">{sortDirection === 'asc' ? '↑' : '↓'}</span>}
        </span>
        {filterable && <ColumnFilter col={col} products={products} filterValues={filterValues} setFilterValues={setFilterValues} />}
      </span>
      <div
        onMouseDown={(e) => startResize(col, e)}
        className="absolute inset-y-0 right-0 w-1 cursor-col-resize hover:bg-indigo-500/30 active:bg-indigo-500/50"
      />
    </th>
  )

  return (
    <>
      {/* ── Table ── */}
      <div className="overflow-x-auto" data-testid="products-totals" style={{ cursor: isResizing ? 'col-resize' : undefined }}>
        {/* table-fixed pins column widths to <colgroup>, ignoring content
            width. Without it, table layout was "auto" and the browser
            grew columns back to the content's intrinsic width regardless
            of <col style="width:30px">. The arbitrary-selector variant
            applies truncate (overflow + ellipsis + nowrap) to every
            data <td>, so cell text clips with ellipsis when the column
            is squeezed below its natural content width. */}
        <table className="w-full table-fixed text-left text-xs text-zinc-900 dark:text-zinc-100 [&>tbody>tr>td]:truncate">
          <colgroup>
            {/* No per-col minWidth — the resize handler already clamps to
                MIN_COL_W (30px). Per-col minWidth here was overriding it
                and stopping users from shrinking columns below the title's
                intrinsic width. Title + cell content both ellipsize via
                truncate now. */}
            {vis('upc') && <col style={{ width: getW('upc') }} />}
            {vis('style_no') && <col style={{ width: getW('style_no') }} />}
            {vis('style_descr') && <col style={{ width: getW('style_descr') }} />}
            {vis('color') && <col style={{ width: getW('color') }} />}
            {vis('size') && <col style={{ width: getW('size') }} />}
            {showPrices && vis('cost_price') && <col style={{ width: getW('cost_price') }} />}
            {showPrices && vis('selling_price') && <col style={{ width: getW('selling_price') }} />}
            {vis('quantity') && <col style={{ width: getW('quantity') }} />}
            {showPrices && vis('tot_cost') && <col style={{ width: getW('tot_cost') }} />}
            {showPrices && vis('tot_selling') && <col style={{ width: getW('tot_selling') }} />}
            {vis('in_stock') && <col style={{ width: getW('in_stock') }} />}
            {showActions && <col style={{ width: getW('actions') }} />}
          </colgroup>
          <thead>
            {/* ── Totals row ── */}
            <tr className="border-b border-zinc-200 bg-zinc-50/50 text-[10px] font-semibold text-zinc-600 dark:border-zinc-700 dark:bg-zinc-800/30 dark:text-zinc-400">
              <th colSpan={['upc','style_no','style_descr','color','size'].filter(c => vis(c)).length || 1} className="py-1.5 pr-2">
                {filteredSorted.length !== products.length && (
                  <span className="mr-2 font-normal text-indigo-500">({filteredSorted.length}/{products.length})</span>
                )}
              </th>
              {showPrices && vis('cost_price') && <th className="py-1.5 pr-2"></th>}
              {showPrices && vis('selling_price') && <th className="py-1.5 pr-2"></th>}
              {vis('quantity') && <th className="py-1.5 pr-2">{totals.quantity}</th>}
              {showPrices && vis('tot_cost') && <th className="py-1.5 pr-2">{currencySymbol}{totals.cost.toFixed(2)}</th>}
              {showPrices && vis('tot_selling') && <th className="py-1.5 pr-2">{currencySymbol}{totals.selling.toFixed(2)}</th>}
              {vis('in_stock') && <th className="py-1.5 pr-2">{totals.received}</th>}
              {showActions && <th className="py-1.5">
                <ColumnVisibility allColumns={allColumns} hiddenCols={hiddenCols} toggleCol={toggleCol} />
              </th>}
            </tr>
            {/* ── Column headers with filter icons ── */}
            <tr className="divide-x divide-zinc-200 border-b border-zinc-200 text-[10px] font-semibold uppercase tracking-wider text-zinc-500 dark:divide-zinc-700 dark:border-zinc-700 dark:text-zinc-400">
              {vis('upc') && sortTh('upc', 'UPC', true)}
              {vis('style_no') && sortTh('style_no', 'Style #', true)}
              {vis('style_descr') && sortTh('style_descr', 'Description', true)}
              {vis('color') && sortTh('color', 'Color', true)}
              {vis('size') && sortTh('size', 'Size', true)}
              {showPrices && vis('cost_price') && sortTh('cost_price', 'Cost')}
              {showPrices && vis('selling_price') && sortTh('selling_price', 'Selling')}
              {vis('quantity') && sortTh('quantity', 'Qty')}
              {showPrices && vis('tot_cost') && <th className="relative overflow-hidden py-2 pl-2 pr-4" style={{ width: getW('tot_cost') }}><span className="block truncate">Tot Cost</span><div onMouseDown={(e) => startResize('tot_cost', e)} className="absolute inset-y-0 right-0 w-1 cursor-col-resize hover:bg-indigo-500/30" /></th>}
              {showPrices && vis('tot_selling') && <th className="relative overflow-hidden py-2 pl-2 pr-4" style={{ width: getW('tot_selling') }}><span className="block truncate">Tot Selling</span><div onMouseDown={(e) => startResize('tot_selling', e)} className="absolute inset-y-0 right-0 w-1 cursor-col-resize hover:bg-indigo-500/30" /></th>}
              {vis('in_stock') && sortTh('in_stock', 'Received')}
              {showActions && <th className="py-2" style={{ width: getW('actions') }}></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {filteredSorted.map((p) => (
              <tr key={p.id} className="group hover:bg-zinc-50 dark:hover:bg-zinc-800/50">
                {vis('upc') && <td className="py-1 pl-2 pr-2"><EditableCell value={p.upc} onSave={(v) => saveField(p.id, 'upc', v)} /></td>}
                {vis('style_no') && <td className="py-1 pl-2 pr-2"><EditableCell value={p.style_no} onSave={(v) => saveField(p.id, 'style_no', v)} /></td>}
                {vis('style_descr') && <td className="py-1 pr-2 truncate"><EditableCell value={p.style_descr} onSave={(v) => saveField(p.id, 'style_descr', v)} /></td>}
                {vis('color') && <td className="py-1 pl-2 pr-2"><EditableCell value={p.color} onSave={(v) => saveField(p.id, 'color', v)} /></td>}
                {vis('size') && <td className="py-1 pl-2 pr-2"><EditableCell value={p.size} onSave={(v) => saveField(p.id, 'size', v)} /></td>}
                {showPrices && vis('cost_price') && <td className="py-1 pl-2 pr-2"><EditableCell value={p.cost_price} onSave={(v) => saveField(p.id, 'cost_price', v)} type="number" /></td>}
                {showPrices && vis('selling_price') && <td className="py-1 pl-2 pr-2"><EditableCell value={p.selling_price} onSave={(v) => saveField(p.id, 'selling_price', v)} type="number" /></td>}
                {vis('quantity') && <td className="py-1 pl-2 pr-2"><EditableCell value={p.quantity} onSave={(v) => saveField(p.id, 'quantity', v)} type="number" /></td>}
                {showPrices && vis('tot_cost') && <td className="py-1 pr-2 text-zinc-500">{currencySymbol}{p.tot_cost.toFixed(2)}</td>}
                {showPrices && vis('tot_selling') && <td className="py-1 pr-2 text-zinc-500">{currencySymbol}{p.tot_selling.toFixed(2)}</td>}
                {vis('in_stock') && <td className="py-1 pl-2 pr-2"><ReceivedEditor product={p} csrfToken={csrfToken} onUpdate={onProductUpdate} /></td>}
                {showActions && (
                  <td className="py-1">
                    {actionColumn ? actionColumn(p) : (
                      <button onClick={() => deleteProduct(p.id)} className="hidden rounded-md border border-zinc-200 p-1 text-zinc-400 hover:border-red-200 hover:bg-red-50 hover:text-red-500 group-hover:inline dark:border-zinc-700 dark:hover:border-red-500/30 dark:hover:bg-red-500/10">
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

// ─── Scanning Panel ──────────────────────────────────────

function ScanningPanel({ purchaseOrder, csrfToken, onNotify, onScanResult, onFullscreen }: {
  purchaseOrder: PurchaseOrder
  csrfToken: string
  onNotify: (msg: string) => void
  onScanResult: (data: { found: boolean; products?: Product[]; product?: Product | null }) => void
  onFullscreen: () => void
}) {
  const [scanning, setScanning] = useState(false)
  const [box, setBox] = useState('1')
  const [lastScan, setLastScan] = useState<{ upc: string; found: boolean } | null>(null)
  const [scanFlash, setScanFlash] = useState(false)
  const upcRef = useRef<HTMLInputElement>(null)

  const handleScan = useCallback(async (upc: string) => {
    if (!upc.trim()) return
    try {
      const res = await fetch(`/purchase_orders/${purchaseOrder.id}/check.json?upc=${encodeURIComponent(upc)}&box_upc=${box}`, {
        headers: { 'X-CSRF-Token': csrfToken },
      })
      const data = await res.json()
      setLastScan({ upc, found: data.found })
      setScanFlash(true)
      setTimeout(() => setScanFlash(false), 1500)
      onScanResult(data)
      if (data.found) {
        const hasExcesses = data.excesses?.some((e: { excesses: number }) => e.excesses > 0)
        onNotify(hasExcesses ? 'warning:Warning:Product scanned — excess' : 'Scanned:Product successfully scanned')
        new Audio(hasExcesses ? '/audio/scan-found.mp3' : '/audio/scan-beep.aac').play().catch(() => {})
      } else {
        onNotify('error:Error:Product not found in order')
        new Audio('/audio/scan-warning.mp3').play().catch(() => {})
      }
    } catch { onNotify('error:Error:Scan failed') }
    if (upcRef.current) { upcRef.current.value = ''; upcRef.current.focus() }
  }, [purchaseOrder.id, box, csrfToken, onNotify, onScanResult])

  const resetScanning = useCallback(async () => {
    if (!confirm('Reset all received quantities to 0?')) return
    await fetch(`/purchase_orders/${purchaseOrder.id}/reset_scanning.json`, {
      method: 'GET', headers: { 'X-CSRF-Token': csrfToken },
    })
    onNotify('Saved:Scanning reset')
    window.location.reload() // need full refresh for products data
  }, [purchaseOrder.id, csrfToken, onNotify])

  const dropBox = useCallback(async () => {
    if (!confirm(`Drop box #${box}? This will remove all quantities from this box.`)) return
    await fetch(`/purchase_orders/${purchaseOrder.id}/drop_box.json`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ box_number: box }),
    })
    onNotify(`Saved:Box #${box} dropped`)
    window.location.reload()
  }, [purchaseOrder.id, box, csrfToken, onNotify])

  const scanControls = (
    <div className="flex items-center justify-center gap-3 transition-all duration-300">
      <button
        onClick={() => { setScanning(!scanning); if (!scanning) setTimeout(() => upcRef.current?.focus(), 200) }}
        className={clsx(
          'shrink-0 rounded-md px-3 py-1.5 text-xs font-medium transition-all duration-300',
          scanning
            ? 'bg-indigo-600 text-white hover:bg-indigo-500'
            : 'border border-zinc-200 text-zinc-600 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800'
        )}
      >
        {scanning ? 'Scanning On' : 'Scanning Off'}
      </button>

      <div className={clsx(
        'flex items-center gap-3 overflow-hidden transition-all duration-300 ease-in-out',
        scanning ? 'max-w-[800px] opacity-100' : 'max-w-0 opacity-0'
      )}>
        <div className="relative w-52 shrink-0">
          <CatalystInput
            ref={upcRef}
            type="text"
            placeholder="Scan UPC..."
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') handleScan(e.currentTarget.value) }}
            tabIndex={scanning ? 0 : -1}
          />
          <button
            type="button"
            onClick={() => { if (upcRef.current) { upcRef.current.value = ''; upcRef.current.focus() } }}
            className="absolute inset-y-0 right-1.5 flex items-center text-zinc-300 hover:text-zinc-500 dark:text-zinc-600 dark:hover:text-zinc-400"
            tabIndex={-1}
          >
            <XClose className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="flex shrink-0 items-center gap-1.5 text-xs">
          <span className="text-zinc-500 whitespace-nowrap">Box #</span>
          <div className="w-20">
            <FormSelect
              value={box}
              onChange={setBox}
              options={Array.from({ length: 50 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }))}
            />
          </div>
        </div>
        <button onClick={() => dropBox()} tabIndex={scanning ? 0 : -1} className="shrink-0 rounded border border-amber-300 px-2 py-1 text-xs font-medium whitespace-nowrap text-amber-700 hover:bg-amber-50 dark:border-amber-500/30 dark:text-amber-400">
          Drop Box
        </button>
        <button onClick={() => resetScanning()} tabIndex={scanning ? 0 : -1} className="shrink-0 rounded border border-red-300 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 dark:border-red-500/30 dark:text-red-400">
          Reset
        </button>
        {lastScan && (
          <span className={clsx(
            'shrink-0 rounded-md px-2 py-1 text-xs font-medium whitespace-nowrap transition-all duration-500',
            scanFlash
              ? (lastScan.found ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400' : 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400')
              : (lastScan.found ? 'text-emerald-600' : 'text-red-500')
          )}>
            {lastScan.upc}: {lastScan.found ? 'Found' : 'Not Found'}
          </span>
        )}
        <button onClick={onFullscreen} tabIndex={scanning ? 0 : -1} className="shrink-0 rounded p-1 text-zinc-400 hover:text-indigo-600 dark:hover:text-indigo-400" title="Fullscreen">
          <ArrowsPointingOutIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  )

  return (
    <div className="mb-4 py-2">
      {scanControls}
    </div>
  )
}

// ─── Main Component ──────────────────────────────────────

export default function ProductsTab({ products: initialProducts, orderId, purchaseOrder, currencySymbol, csrfToken, onNotify }: ProductsTabProps) {
  const [products, setProducts] = useState(initialProducts)
  const [subTab, setSubTab] = useState<'list' | 'excesses' | 'not_found'>('list')
  const [fullscreen, setFullscreen] = useState(false)
  const showPrices = true


  const allFound = products.filter(isAllFound)
  const excesses = products.filter(p => isAllFound(p) && p.in_stock > p.quantity && !p.accepted)
  const notFound = products.filter(isNotFound)

  const excessesCount = excesses.reduce((sum, p) => sum + p.excesses_count, 0)
  const notFoundCount = notFound.reduce((sum, p) => sum + p.in_stock, 0)

  const updateProduct = useCallback((updated: Product) => {
    setProducts(prev => prev.map(p => p.id === updated.id ? updated : p))
  }, [])

  const deleteProduct = useCallback((id: number) => {
    setProducts(prev => prev.filter(p => p.id !== id))
  }, [])

  const handleScanResult = useCallback((data: { found: boolean; products?: Product[]; product?: Product | null }) => {
    if (data.found && data.products) {
      data.products.forEach(updateProduct)
    } else if (!data.found && data.product) {
      setProducts(prev => [...prev, data.product!])
    }
  }, [updateProduct])

  const acceptNotFound = useCallback((productId: number) => {
    fetch(`/products/${productId}/to_purchase_order.json`, { headers: { 'X-CSRF-Token': csrfToken } })
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) { updateProduct(data); onNotify('Saved:Product accepted') } })
  }, [csrfToken, updateProduct, onNotify])

  const acceptExcess = useCallback((productId: number) => {
    fetch(`/products/${productId}/accept_excess.json`, { headers: { 'X-CSRF-Token': csrfToken } })
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) { updateProduct(data); onNotify('Saved:Product accepted') } })
  }, [csrfToken, updateProduct, onNotify])

  const acceptAllNotFound = useCallback(() => {
    if (!confirm('Accept all not-found products into the order?')) return
    fetch(`/accept_all_not_found/${orderId}.json`, { headers: { 'X-CSRF-Token': csrfToken } })
      .then(() => { window.location.reload() })
  }, [orderId, csrfToken])

  const acceptAllExcesses = useCallback(() => {
    if (!confirm('Accept all excess quantities?')) return
    fetch(`/accept_all_excesses/${orderId}.json`, { headers: { 'X-CSRF-Token': csrfToken } })
      .then(() => { window.location.reload() })
  }, [orderId, csrfToken])

  const content = (
    <div data-testid="products-tab" className={fullscreen ? 'flex-1 overflow-y-auto p-6' : ''}>
      {/* Scanning panel */}
      {purchaseOrder && (
        <ScanningPanel
          purchaseOrder={purchaseOrder}
          csrfToken={csrfToken}
          onNotify={onNotify}
          onScanResult={handleScanResult}
          onFullscreen={() => setFullscreen(!fullscreen)}
        />
      )}

      {/* Sub-tabs */}
      <div className="mb-3 flex gap-1 border-b border-zinc-200 dark:border-zinc-700">
        <button
          onClick={() => setSubTab('list')}
          className={clsx(
            'border-b-2 px-3 py-1.5 text-xs font-medium transition-colors',
            subTab === 'list' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-zinc-500 hover:text-zinc-700'
          )}
        >
          Products <Badge color="zinc" className="ml-1">{allFound.length}</Badge>
        </button>
        <button
          onClick={() => setSubTab('excesses')}
          className={clsx(
            'border-b-2 px-3 py-1.5 text-xs font-medium transition-colors',
            subTab === 'excesses' ? 'border-amber-500 text-amber-600' : 'border-transparent text-zinc-500 hover:text-zinc-700'
          )}
        >
          Excesses {excessesCount > 0 && <Badge color="amber" className="ml-1">{excessesCount}</Badge>}
        </button>
        <button
          onClick={() => setSubTab('not_found')}
          className={clsx(
            'border-b-2 px-3 py-1.5 text-xs font-medium transition-colors',
            subTab === 'not_found' ? 'border-red-500 text-red-600' : 'border-transparent text-zinc-500 hover:text-zinc-700'
          )}
        >
          Not Found {notFoundCount > 0 && <Badge color="red" className="ml-1">{notFoundCount}</Badge>}
        </button>
      </div>

      {/* Sub-tab content */}
      {subTab === 'list' && (
        <ProductsTable
          products={allFound}
          csrfToken={csrfToken}
          currencySymbol={currencySymbol}
          onNotify={onNotify}
          onProductUpdate={updateProduct}
          onProductDelete={deleteProduct}
          showPrices={showPrices}
        />
      )}

      {subTab === 'excesses' && (
        <>
          {excesses.length > 0 && (
            <div className="mb-2 flex justify-end">
              <button onClick={acceptAllExcesses} className="rounded bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-500">
                Accept All Excesses
              </button>
            </div>
          )}
          <ProductsTable
            products={excesses}
            csrfToken={csrfToken}
            currencySymbol={currencySymbol}
            onNotify={onNotify}
            onProductUpdate={updateProduct}
            onProductDelete={deleteProduct}
            showPrices={showPrices}
            actionColumn={(p) => (
              <div className="flex items-center gap-1">
                <button onClick={() => acceptExcess(p.id)} className="rounded-md border border-emerald-200 p-1 text-emerald-500 hover:bg-emerald-50 hover:text-emerald-700 dark:border-emerald-500/30 dark:hover:bg-emerald-500/10" title="Accept excess">
                  <CheckIcon className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => { if (confirm('Delete?')) deleteProduct(p.id) }} className="rounded-md border border-zinc-200 p-1 text-zinc-400 hover:border-red-200 hover:bg-red-50 hover:text-red-500 dark:border-zinc-700 dark:hover:border-red-500/30 dark:hover:bg-red-500/10">
                  <TrashIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          />
        </>
      )}

      {subTab === 'not_found' && (
        <>
          {notFound.length > 0 && (
            <div className="mb-2 flex justify-end">
              <button onClick={acceptAllNotFound} className="rounded bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-500">
                Accept All to Order
              </button>
            </div>
          )}
          <ProductsTable
            products={notFound}
            csrfToken={csrfToken}
            currencySymbol={currencySymbol}
            onNotify={onNotify}
            onProductUpdate={updateProduct}
            onProductDelete={deleteProduct}
            showPrices={showPrices}
            actionColumn={(p) => (
              <div className="flex items-center gap-1">
                <button onClick={() => acceptNotFound(p.id)} className="rounded-md border border-emerald-200 p-1 text-emerald-500 hover:bg-emerald-50 hover:text-emerald-700 dark:border-emerald-500/30 dark:hover:bg-emerald-500/10" title="Accept to order">
                  <CheckIcon className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => { if (confirm('Delete?')) deleteProduct(p.id) }} className="rounded-md border border-zinc-200 p-1 text-zinc-400 hover:border-red-200 hover:bg-red-50 hover:text-red-500 dark:border-zinc-700 dark:hover:border-red-500/30 dark:hover:bg-red-500/10">
                  <TrashIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          />
        </>
      )}

    </div>
  )

  return (
    <div
      className={clsx(
        'transition-all duration-300 ease-in-out',
        fullscreen
          ? 'fixed inset-0 z-[200] flex flex-col rounded-t-2xl bg-white shadow-2xl dark:bg-zinc-900'
          : ''
      )}
    >
      {fullscreen && (
        <div data-testid="fullscreen-scanning-header" className="flex shrink-0 items-center justify-between border-b border-zinc-200 px-6 py-3 dark:border-zinc-700">
          <h2 className="text-sm font-semibold text-zinc-900 dark:text-white">
            Scanning <span className="text-zinc-400">— Order #{orderId}</span>
          </h2>
          <button onClick={() => setFullscreen(false)} className="rounded p-1 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300">
            <XClose className="h-5 w-5" />
          </button>
        </div>
      )}
      {content}
    </div>
  )
}
